import { Injectable, signal, computed, inject } from '@angular/core';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import {
  BpmnPropertiesPanelModule,
  BpmnPropertiesProviderModule,
  ZeebePropertiesProviderModule,
} from 'bpmn-js-properties-panel';
// @ts-ignore
import ZeebeBehaviorsModule from 'camunda-bpmn-js-behaviors/lib/camunda-cloud';
// @ts-ignore
import zeebeModdle from 'zeebe-bpmn-moddle/resources/zeebe.json';
// @ts-ignore
import bpmnlintConfig from './bundled-config.js';
import lintModule from 'bpmn-js-bpmnlint';
import {
  BpmnLintService,
  BpmnLintResult,
  BpmnLintIssue,
} from './bpmn-lint.service';

export interface BpmnModelerConfig {
  container: string | HTMLElement;
  propertiesPanel?: string | HTMLElement;
  width?: string | number;
  height?: string | number;
  additionalModules?: any[];
  moddleExtensions?: any;
}

@Injectable({
  providedIn: 'root',
})
export class BpmnModelerService {
  private modeler: BpmnModeler | null = null;
  private readonly destroy$ = new BehaviorSubject<boolean>(false);
  private readonly lintService = inject(BpmnLintService);

  // Signals for reactive state management
  private readonly _isInitialized = signal(false);
  private readonly _hasUnsavedChanges = signal(false);
  private readonly _currentXml = signal<string>('');
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _lintOverlayVisible = signal(false);
  private readonly _lintingActive = signal(true);

  // Public computed signals
  readonly isInitialized = computed(() => this._isInitialized());
  readonly hasUnsavedChanges = computed(() => this._hasUnsavedChanges());
  readonly currentXml = computed(() => this._currentXml());
  readonly isLoading = computed(() => this._isLoading());
  readonly lintOverlayVisible = computed(() => this._lintOverlayVisible());
  readonly lintingActive = computed(() => this._lintingActive());
  readonly error = computed(() => this._error());

  /**
   * Initialize the BPMN modeler
   */
  async initialize(config: BpmnModelerConfig): Promise<void> {
    try {
      this._isLoading.set(true);
      this._error.set(null);

      // Create new modeler instance with properties panel
      this.modeler = new BpmnModeler({
        container: config.container,
        linting: {
          bpmnlint: bpmnlintConfig,
        },
        width: config.width || '100%',
        height: config.height || '100%',
        additionalModules: [
          BpmnPropertiesPanelModule,
          BpmnPropertiesProviderModule,
          ZeebePropertiesProviderModule,
          ZeebeBehaviorsModule,
          lintModule,
        ],
        moddleExtensions: {
          zeebe: zeebeModdle,
        },
        propertiesPanel: config.propertiesPanel
          ? {
              parent: config.propertiesPanel,
              layout: {
                groups: {
                  general: {
                    open: true,
                  },
                },
              },
            }
          : undefined,
      });

      // Set up linting event listeners immediately after modeler creation
      this.setupLintingListeners();

      // Load empty BPMN diagram first
      await this.loadEmptyDiagram();

      // Wait a bit for the modeler to be fully ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Set up event listeners after the modeler is fully initialized
      this.setupEventListeners();

      // Trigger initial linting check to get current results
      await this.triggerLintingCheck();

      // Set up linting overlay detection (disabled for now due to container access issues)
      // this.setupLintingOverlayDetection();

      this._isInitialized.set(true);
      this._isLoading.set(false);
    } catch (error) {
      this._error.set(`Failed to initialize BPMN modeler: ${error}`);
      this._isLoading.set(false);
      throw error;
    }
  }

  /**
   * Generate a random process ID in the format Process_XXXXXXXXXX
   */
  private generateRandomProcessId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'Process_';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Load empty BPMN diagram
   */
  private async loadEmptyDiagram(): Promise<void> {
    const processId = this.generateRandomProcessId();
    const emptyXml = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" 
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" 
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" 
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_1" 
                  targetNamespace="http://bpmn.io/schema/bpmn" 
                  exporter="ng-bpmn" 
                  exporterVersion="8.7.1">
  <bpmn:process id="${processId}" isExecutable="true">
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${processId}">
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

    await this.importXml(emptyXml);
  }

  /**
   * Set up linting event listeners
   */
  private setupLintingListeners(): void {
    if (!this.modeler) return;

    try {
      // Listen for linting events using the correct .on() method
      // @ts-ignore
      this.modeler.on('linting.completed', (event: any) => {
        this.handleLintingResults(event);
      });

      // Listen for linting overlay toggle events (when user toggles overlay visibility)
      // @ts-ignore
      this.modeler.on('linting.toggled', (event: any) => {
        // This event is for overlay visibility, not linting functionality
        // Linting continues to run in the background
        this._lintOverlayVisible.set(event.active);
      });
    } catch (error) {
      // Failed to setup linting listeners
    }
  }

  /**
   * Trigger linting check manually to get current results
   */
  private async triggerLintingCheck(): Promise<void> {
    if (!this.modeler) return;

    try {
      // Wait a bit more to ensure the modeler is fully ready
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Try to trigger linting manually
      // @ts-ignore
      const linting = this.modeler.get('linting');
      if (linting && typeof linting.lint === 'function') {
        await linting.lint();
      }
    } catch (error) {
      // Failed to trigger linting check
    }
  }

  /**
   * Setup linting overlay detection
   */
  private setupLintingOverlayDetection(): void {
    if (!this.modeler) return;

    try {
      // Check for linting overlay visibility periodically
      // This is a workaround since bpmn-js-bpmnlint doesn't expose overlay state directly
      setInterval(() => {
        if (this.modeler) {
          try {
            // Try to access the container element from the modeler's internal structure
            const containerElement = (this.modeler as any)._container;
            if (containerElement) {
              const lintOverlay =
                containerElement.querySelector('.bjs-lint-overlay');
              const isVisible =
                lintOverlay && lintOverlay.style.display !== 'none';
              this._lintOverlayVisible.set(isVisible);
            }
          } catch (containerError) {
            // If direct access fails, try to find the overlay in the document
            const lintOverlay = document.querySelector('.bjs-lint-overlay');
            if (lintOverlay) {
              const isVisible =
                lintOverlay &&
                (lintOverlay as HTMLElement).style.display !== 'none';
              this._lintOverlayVisible.set(isVisible);
            }
          }
        }
      }, 500); // Check every 500ms
    } catch (error) {
      // Failed to setup linting overlay detection
    }
  }

  /**
   * Handle linting results
   */
  private handleLintingResults(results: any): void {
    let issues: BpmnLintIssue[] = [];

    // Handle the actual structure: { issues: { elementId: [issue1, issue2, ...] } }
    if (results && typeof results === 'object' && results.issues) {
      const issuesObject = results.issues;

      // Flatten all issues from all elements into a single array
      issues = Object.keys(issuesObject).flatMap((elementId: string) => {
        const elementIssues = issuesObject[elementId];
        return elementIssues.map((issue: any) => ({
          id: issue.id || elementId,
          message: issue.message || 'Unknown issue',
          severity: issue.category || 'warn', // bpmnlint uses 'category' instead of 'severity'
          elementId: elementId,
          elementType: this.getElementTypeFromId(elementId),
          rule: issue.rule,
          path: issue.path,
          meta: issue.meta,
        }));
      });
    } else if (Array.isArray(results)) {
      // Fallback for array format
      issues = results.map((result: any) => ({
        id: result.id || 'unknown',
        message: result.message || 'Unknown issue',
        severity: result.severity || result.category || 'warn',
        elementId: result.elementId,
        elementType: result.elementType,
        rule: result.rule,
        path: result.path,
        meta: result.meta,
      }));
    }

    const lintResult: BpmnLintResult = {
      issues,
      hasErrors: issues.some((issue) => issue.severity === 'error'),
      hasWarnings: issues.some((issue) => issue.severity === 'warn'),
      hasInfo: issues.some((issue) => issue.severity === 'info'),
    };

    this.lintService.updateLintResults(lintResult);
  }

  /**
   * Extract element type from element ID
   */
  private getElementTypeFromId(elementId: string): string {
    if (elementId.startsWith('Process_')) return 'process';
    if (elementId.startsWith('Event_')) return 'event';
    if (elementId.startsWith('Task_')) return 'task';
    if (elementId.startsWith('Gateway_')) return 'gateway';
    if (elementId.startsWith('SequenceFlow_')) return 'sequenceFlow';
    return 'unknown';
  }

  /**
   * Set up event listeners for the modeler
   */
  private setupEventListeners(): void {
    if (!this.modeler) return;

    try {
      // Listen for import events
      fromEvent(this.modeler as any, 'import.done')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: any) => {
          if (event.error) {
            this._error.set(`Import failed: ${event.error.message}`);
          } else {
            this._error.set(null);
            this._hasUnsavedChanges.set(false);
          }
        });

      // Listen for command stack changes to track unsaved changes
      // Use a more robust approach with setTimeout to ensure command stack is ready
      setTimeout(() => {
        try {
          const commandStack = this.modeler?.get('commandStack') as any;
          if (commandStack && typeof commandStack.on === 'function') {
            fromEvent(commandStack, 'changed')
              .pipe(debounceTime(300), takeUntil(this.destroy$))
              .subscribe(() => {
                this._hasUnsavedChanges.set(true);
              });
          }
        } catch (error) {
          // Failed to setup command stack listener
        }
      }, 200);
    } catch (error) {
      // Failed to setup event listeners - continue without some event listeners
    }
  }

  /**
   * Import BPMN XML
   */
  async importXml(xml: string): Promise<void> {
    if (!this.modeler) {
      throw new Error('Modeler not initialized');
    }

    try {
      await this.modeler.importXML(xml);
      this._currentXml.set(xml);
      this._hasUnsavedChanges.set(false);

      // Ensure the process ID from the imported XML is preserved
      await this.preserveProcessIdFromXml(xml);
    } catch (error) {
      this._error.set(`Import failed: ${error}`);
      throw error;
    }
  }

  /**
   * Extract and preserve the process ID from imported XML
   */
  private async preserveProcessIdFromXml(xml: string): Promise<void> {
    if (!this.modeler) return;

    try {
      // Parse the XML to extract the process ID
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      const processElement = xmlDoc.querySelector('bpmn\\:process, process');

      if (processElement) {
        const processId = processElement.getAttribute('id');
        const processName = processElement.getAttribute('name');

        if (processId) {
          // Wait a bit for the modeler to be ready
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Get the root element (process) from the modeler
          const elementRegistry = this.modeler.get('elementRegistry') as any;
          const processElements = elementRegistry.filter(
            (element: any) => element.type === 'bpmn:Process'
          );

          if (processElements.length > 0) {
            const processElement = processElements[0];
            const modeling = this.modeler.get('modeling') as any;

            // Update the process ID if it's different
            if (processElement.id !== processId) {
              modeling.updateProperties(processElement, { id: processId });
            }

            // Update the process name if it's different and provided
            if (
              processName &&
              processElement.businessObject.name !== processName
            ) {
              modeling.updateProperties(processElement, { name: processName });
            }
          }
        }
      }
    } catch (error) {
      console.warn('Failed to preserve process ID from imported XML:', error);
      // Don't throw error as this is not critical for the import to succeed
    }
  }

  /**
   * Export BPMN XML
   */
  async exportXml(): Promise<{ xml: string; error?: any }> {
    if (!this.modeler) {
      throw new Error('Modeler not initialized');
    }

    try {
      const result = await this.modeler.saveXML({ format: true });
      this._currentXml.set(result.xml || '');
      this._hasUnsavedChanges.set(false);
      return { xml: result.xml || '', error: result.error };
    } catch (error) {
      this._error.set(`Export failed: ${error}`);
      return { xml: '', error };
    }
  }

  /**
   * Export BPMN as SVG
   */
  async exportSvg(): Promise<{ svg: string; error?: any }> {
    if (!this.modeler) {
      throw new Error('Modeler not initialized');
    }

    try {
      const result = await this.modeler.saveSVG();
      return { svg: result.svg || '' };
    } catch (error) {
      this._error.set(`SVG export failed: ${error}`);
      return { svg: '', error };
    }
  }

  /**
   * Get the modeler instance
   */
  getModeler(): BpmnModeler | null {
    return this.modeler;
  }

  /**
   * Check if modeler is initialized
   */
  isModelerInitialized(): boolean {
    return this._isInitialized();
  }

  /**
   * Get current XML content
   */
  getCurrentXml(): string {
    return this._currentXml();
  }

  /**
   * Get current error state
   */
  getError(): string | null {
    return this._error();
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Clear the modeler (reset to empty state)
   */
  async clear(): Promise<void> {
    await this.loadEmptyDiagram();
  }

  /**
   * Get lint results observable
   */
  getLintResults$(): Observable<BpmnLintResult> {
    return this.lintService.lintResults$;
  }

  /**
   * Get current lint results
   */
  getCurrentLintResults(): BpmnLintResult {
    return this.lintService.getCurrentLintResults();
  }

  /**
   * Clear lint results
   */
  clearLintResults(): void {
    this.lintService.clearLintResults();
  }

  /**
   * Get the lint service instance
   */
  getLintService(): BpmnLintService {
    return this.lintService;
  }

  /**
   * Manually trigger linting check
   */
  async triggerLinting(): Promise<void> {
    await this.triggerLintingCheck();
  }

  /**
   * Get current linting results directly from the modeler
   */
  async getCurrentLintingResults(): Promise<any> {
    if (!this.modeler) return null;

    try {
      // @ts-ignore
      const linting = this.modeler.get('linting');
      if (linting && typeof linting.getLintResults === 'function') {
        return await linting.getLintResults();
      } else if (linting && typeof linting.lint === 'function') {
        // If getLintResults is not available, trigger lint and wait for results
        await linting.lint();
        return null; // Results will come through the event listener
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Enable linting overlays
   */
  enableLintingOverlays(): void {
    if (!this.modeler) return;

    try {
      // @ts-ignore
      const linting = this.modeler.get('linting');
      if (linting && typeof linting.enable === 'function') {
        linting.enable();
        this._lintOverlayVisible.set(true);
      }
    } catch (error) {
      // Failed to enable linting overlays
    }
  }

  /**
   * Disable linting overlays (but keep linting functionality active)
   */
  disableLintingOverlays(): void {
    if (!this.modeler) return;

    try {
      // @ts-ignore
      const linting = this.modeler.get('linting');
      if (linting && typeof linting.disable === 'function') {
        linting.disable();
        this._lintOverlayVisible.set(false);
      }
      // Note: We don't clear linting results as linting continues in background
    } catch (error) {
      // Failed to disable linting overlays
    }
  }

  /**
   * Toggle linting state
   */
  toggleLinting(): void {
    if (!this.modeler) return;

    try {
      // @ts-ignore
      const linting = this.modeler.get('linting');
      if (linting && typeof linting.toggle === 'function') {
        linting.toggle();
      } else {
        // Fallback: try to get current state and toggle manually
        // @ts-ignore
        const isActive =
          linting && linting.isActive ? linting.isActive() : false;
        if (isActive) {
          this.disableLintingOverlays();
        } else {
          this.enableLintingOverlays();
        }
      }
    } catch (error) {
      // Failed to toggle linting
    }
  }

  /**
   * Check if linting is currently active
   */
  isLintingActive(): boolean {
    if (!this.modeler) return false;

    try {
      // @ts-ignore
      const linting = this.modeler.get('linting');
      if (linting && typeof linting.isActive === 'function') {
        return linting.isActive();
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Zoom in the canvas
   */
  zoomIn(): void {
    if (!this.modeler) {
      return;
    }

    try {
      const zoomScroll = this.modeler.get('zoomScroll') as any;
      if (zoomScroll && typeof zoomScroll.stepZoom === 'function') {
        zoomScroll.stepZoom(1);
      }
    } catch (error) {
      // Failed to zoom in
    }
  }

  /**
   * Zoom out the canvas
   */
  zoomOut(): void {
    if (!this.modeler) {
      return;
    }

    try {
      const zoomScroll = this.modeler.get('zoomScroll') as any;
      if (zoomScroll && typeof zoomScroll.stepZoom === 'function') {
        zoomScroll.stepZoom(-1);
      }
    } catch (error) {
      // Failed to zoom out
    }
  }

  /**
   * Fit the diagram to viewport
   */
  fitToViewport(): void {
    if (!this.modeler) {
      return;
    }

    try {
      const canvas = this.modeler.get('canvas') as any;
      if (canvas && typeof canvas.zoom === 'function') {
        canvas.zoom('fit-viewport');
      }
    } catch (error) {
      // Failed to fit to viewport
    }
  }

  /**
   * Reset zoom to 100%
   */
  resetZoom(): void {
    if (!this.modeler) {
      return;
    }

    try {
      const canvas = this.modeler.get('canvas') as any;
      if (canvas && typeof canvas.zoom === 'function') {
        canvas.zoom(1);
      }
    } catch (error) {
      // Failed to reset zoom
    }
  }

  /**
   * Get current zoom level
   */
  getCurrentZoom(): number {
    if (!this.modeler) {
      return 1;
    }

    try {
      const canvas = this.modeler.get('canvas') as any;
      if (canvas && typeof canvas.zoom === 'function') {
        return canvas.zoom();
      }
      return 1;
    } catch (error) {
      return 1;
    }
  }

  /**
   * Destroy the modeler
   */
  destroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.modeler?.destroy();
    this.modeler = null;
    this.lintService.clearLintResults();
  }
}
